import type { ReactNode } from "react";
import { format } from "date-fns";
import { AlertCircle, CheckCircle2, Clock } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import type { CheckerHealth, IndexSummary } from "@/types/indexation";
import type { IndexGrain } from "./indexationColumns";

/**
 * Checker health + coverage as badges, in the same language as the Clicks /GSC
 * sync badges (PipelineFreshness / GscFreshnessBadge): a pill per signal with a
 * tone (positive / warning / neutral), an icon and a bold value. Freshness falls
 * to warning under FRESH_WARN; the errors pill shows only when there are errors.
 * Each pill has a CSS hover tooltip with the detail behind it (no dependency —
 * group-hover on a popover-styled box). Coverage (tracked / untracked) is a
 * denominator, not a status, so it stays a muted trailer rather than a colored pill.
 */

const TZ_LABEL = `UTC${format(new Date(), "xxx")}`;
const fmtDate = (iso: string | null) => (iso ? format(new Date(iso), "dd.MM.yyyy HH:mm") : "—");
const fmtInt = (n: number | null | undefined) => (n == null ? "—" : n.toLocaleString());
const FRESH_WARN = 90; // % of a tier re-checked within its own cadence below which it's a warning

const PILL = "rounded-full border px-2.5 py-1 font-normal";

/** Detail-on-hover, zero-dependency: a popover-styled box shown on group-hover. */
function Tip({
  content,
  align = "center",
  children,
}: {
  content: ReactNode;
  align?: "center" | "right";
  children: ReactNode;
}) {
  return (
    <span className="group relative inline-flex">
      {children}
      <span
        role="tooltip"
        className={
          "pointer-events-none absolute bottom-full z-50 mb-2 hidden w-max max-w-xs rounded-md border border-border " +
          "bg-popover px-3 py-2 text-left text-xs leading-relaxed text-popover-foreground shadow-md group-hover:block " +
          (align === "right" ? "right-0" : "left-1/2 -translate-x-1/2")
        }
      >
        {content}
      </span>
    </span>
  );
}

function Pill({
  tone,
  icon: Icon,
  label,
  value,
  detail,
  align,
}: {
  tone: "positive" | "warning" | "neutral";
  icon: typeof CheckCircle2;
  label: string;
  value?: string;
  detail: ReactNode;
  align?: "center" | "right";
}) {
  const card = tone === "neutral" ? "border-border" : `signal-card-${tone}`;
  const text = tone === "neutral" ? "text-muted-foreground" : `signal-text-${tone}`;
  return (
    <Tip content={detail} align={align}>
      <Badge variant="outline" className={`${PILL} ${card} cursor-help`}>
        <Icon className={`size-3.5 ${text}`} />
        <span className={text}>
          {label}
          {value != null ? <> <span className="font-semibold tabular-nums">{value}</span></> : null}
        </span>
      </Badge>
    </Tip>
  );
}

/** Cause breakdown as a bullet list for a badge tooltip. */
function CauseBullets({ items }: { items: { cause: string; n: number }[] }) {
  if (items.length === 0) return null;
  return (
    <ul className="mt-1 list-disc space-y-0.5 pl-4">
      {items.map((g) => (
        <li key={g.cause}>
          <span className="font-medium tabular-nums">{fmtInt(g.n)}</span> · {g.cause}
        </li>
      ))}
    </ul>
  );
}

export function IndexationFreshness({
  health,
  summary,
  grain,
}: {
  health?: CheckerHealth;
  summary?: IndexSummary;
  grain: IndexGrain;
}) {
  const checks = health?.checks_24h ?? 0;
  // Honest split: hard = real failures (alarm); empty = Bright Data's transient
  // under-floor stub, shown as neutral provider flakiness, not a problem.
  const errorsHard = summary?.errors_hard_24h ?? 0;
  const errorBreakdown = summary?.errors_breakdown ?? [];
  const dead = checks === 0;

  // Per-tier freshness, each against its own cadence (T1 24h / T2 7d) — not blended.
  const pct = (fresh?: number, total?: number) =>
    total && total > 0 ? Math.round(((fresh ?? 0) / total) * 100) : null;
  const t1Total = summary?.domains_tier1;
  const t2Total = summary?.domains_tier2;
  const t1Pct = pct(summary?.domains_tier1_fresh, t1Total);
  const t2Pct = pct(summary?.domains_tier2_fresh, t2Total);

  const isUrl = grain === "url";
  const total = isUrl ? summary?.urls : summary?.domains;
  const untracked = isUrl ? summary?.urls_untracked : summary?.domains_untracked;

  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      {/* Checker alive / down — value is the last check time. */}
      <Pill
        tone={dead ? "warning" : "positive"}
        icon={dead ? AlertCircle : CheckCircle2}
        label={dead ? "Checker down" : "Checked"}
        value={dead ? undefined : `${fmtDate(health?.last_check_at ?? null)} (${TZ_LABEL})`}
        detail={
          <>
            <div className="font-medium text-foreground">{dead ? "Checker not running" : "Checker running"}</div>
            <div>Last check: {fmtDate(health?.last_check_at ?? null)} ({TZ_LABEL})</div>
            <div>{fmtInt(checks)} checks · {fmtInt(errorsHard)} errors in the last 24h</div>
            {dead ? <div className="signal-text-warning">No checks in 24h — the checker may be stopped.</div> : null}
          </>
        }
      />

      {/* Coverage freshness per tier — each against its OWN cadence, not blended.
          ponytail: window labels (24h / 7d) mirror dmca_settings; update if cadence changes. */}
      {(
        [
          { lbl: "T1 Fresh", p: t1Pct, fresh: summary?.domains_tier1_fresh, total: t1Total, tier: "tier-1", win: "24h" },
          { lbl: "T2 Fresh", p: t2Pct, fresh: summary?.domains_tier2_fresh, total: t2Total, tier: "tier-2", win: "7 days" },
        ] as const
      ).map((f) =>
        f.p == null ? null : (
          <Pill
            key={f.lbl}
            tone={f.p < FRESH_WARN ? "warning" : "positive"}
            icon={f.p < FRESH_WARN ? AlertCircle : CheckCircle2}
            label={f.lbl}
            value={`${f.p}%`}
            detail={
              <>
                <div className="font-medium text-foreground">{f.lbl} coverage</div>
                <div>{fmtInt(f.fresh)} of {fmtInt(f.total)} {f.tier} domains re-checked within {f.win} (their cadence).</div>
                <div>Below {FRESH_WARN}% is treated as stale.</div>
              </>
            }
          />
        ),
      )}

      {/* Errors — real failures only (empty provider stubs are shown separately). */}
      {errorsHard > 0 ? (
        <Pill
          tone="warning"
          icon={AlertCircle}
          label="Errors"
          value={fmtInt(errorsHard)}
          detail={
            <>
              <div className="font-medium text-foreground">{fmtInt(errorsHard)} failed checks in the last 24h</div>
              <CauseBullets items={errorBreakdown} />
              <div className="mt-1">Real provider / network / auth failures (5xx, timeouts). They retry on the next cycle; a known verdict is kept meanwhile.</div>
            </>
          }
        />
      ) : null}

      {/* Coverage — a denominator, muted trailer (not a status pill). */}
      <Tip
        align="right"
        content={
          <>
            <div><span className="font-medium text-foreground">{fmtInt(total)}</span> tracked — tier 1 + 2, actively re-checked.</div>
            <div><span className="font-medium text-foreground">{fmtInt(untracked)}</span> untracked — tier 3 (no money in the window), not checked by design.</div>
          </>
        }
      >
        <span className="cursor-help text-muted-foreground">
          <Clock className="mr-1 inline size-3 align-[-1px]" />
          {fmtInt(total)} {isUrl ? "URLs" : "domains"} tracked · {fmtInt(untracked)} untracked (Tier 3)
        </span>
      </Tip>
    </div>
  );
}
